<!--
  DistributorPayoutsPage -- Distributor App Payouts page.

  Plan: planning/gap-analysis/2026-07-01/plan-5-distributor-surface-odds-and-ends.md
  Item: 5.1 Payouts interim

  Replaces DistributorComingSoon at /distributor/reporting/payouts.
  Fetches payout data derived from order_split rows for all scoped orders.

  Backend contract (consumed, not modified):
    GET /api/reporting/distributor-payouts
      -> {
           scope: "owner" | "builder",
           summary: {
             gross_revenue_cents: <int>,
             platform_fees_cents: <int>,
             distributor_net_cents: <int>
           },
           orders: [
             {
               order_id: <int>,
               order_number: <str | null>,
               store_name: <str>,
               placed_at: "<iso8601>",
               gross_revenue_cents: <int>,
               platform_fees_cents: <int>,
               distributor_net_cents: <int>,
               split_status: "complete" | "pending"
             }
           ]
         }

  Capability gate: distributor.reporting.read (both Owner and Builder).
  No pagination (pass-1 interim; volume expected to be low during beta).
-->
<template>
  <section class="dpay" data-testid="dist-payouts-page">
    <header class="dpay__head">
      <h1 class="dpay__title">Payouts</h1>
      <p
        v-if="scope"
        class="dpay__scope"
        data-testid="dist-payouts-scope"
      >
        {{ scopeLabel }}
      </p>
    </header>

    <p
      v-if="loading"
      class="dpay__loading"
      data-testid="dist-payouts-loading"
      role="status"
    >
      Loading payouts...
    </p>

    <p
      v-else-if="loadError"
      class="dpay__error"
      data-testid="dist-payouts-error"
      role="alert"
    >
      We could not load your payouts. Please reload the page and try again.
    </p>

    <template v-else>
      <!-- Summary card -->
      <div class="dpay__summary" data-testid="dist-payouts-summary">
        <div class="dpay__summary-item">
          <span class="dpay__summary-label">Gross Revenue</span>
          <span
            class="dpay__summary-value"
            data-testid="dist-payouts-gross"
          >{{ formatCents(summary.gross_revenue_cents) }}</span>
        </div>
        <div class="dpay__summary-item">
          <span class="dpay__summary-label">Platform Fees</span>
          <span
            class="dpay__summary-value dpay__summary-value--fees"
            data-testid="dist-payouts-fees"
          >{{ formatCents(summary.platform_fees_cents) }}</span>
        </div>
        <div class="dpay__summary-item dpay__summary-item--net">
          <span class="dpay__summary-label">Your Net</span>
          <span
            class="dpay__summary-value dpay__summary-value--net"
            data-testid="dist-payouts-net"
          >{{ formatCents(summary.distributor_net_cents) }}</span>
        </div>
      </div>

      <p
        v-if="orders.length === 0"
        class="dpay__empty"
        data-testid="dist-payouts-empty"
        role="status"
      >
        No orders yet. Payout data for orders placed in your stores will
        appear here.
      </p>

      <table
        v-else
        class="dpay__table"
        data-testid="dist-payouts-table"
      >
        <thead>
          <tr>
            <th class="dpay__th">Order</th>
            <th class="dpay__th">Store</th>
            <th class="dpay__th">Date</th>
            <th class="dpay__th dpay__th--num">Gross</th>
            <th class="dpay__th dpay__th--num">Fees</th>
            <th class="dpay__th dpay__th--num">Net</th>
            <th class="dpay__th">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="order in orders"
            :key="order.order_id"
            class="dpay__row"
            :data-testid="`dist-payouts-row-${order.order_id}`"
          >
            <td class="dpay__td">
              {{ order.order_number || '#' + order.order_id }}
            </td>
            <td class="dpay__td">{{ order.store_name }}</td>
            <td class="dpay__td">{{ formatDate(order.placed_at) }}</td>
            <td class="dpay__td dpay__td--num">
              {{ formatCents(order.gross_revenue_cents) }}
            </td>
            <td class="dpay__td dpay__td--num">
              {{ formatCents(order.platform_fees_cents) }}
            </td>
            <td class="dpay__td dpay__td--num">
              {{ formatCents(order.distributor_net_cents) }}
            </td>
            <td class="dpay__td">
              <span
                class="dpay__status"
                :class="`dpay__status--${order.split_status}`"
                :data-testid="`dist-payouts-status-${order.order_id}`"
              >
                {{ order.split_status === 'complete' ? 'Complete' : 'Pending' }}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </template>
  </section>
</template>

<script>
import { getDistributorPayouts } from "@/services/distributorService";

const EMPTY_SUMMARY = {
  gross_revenue_cents: 0,
  platform_fees_cents: 0,
  distributor_net_cents: 0,
};

export default {
  name: "DistributorPayoutsPage",
  data() {
    return {
      scope: "",
      summary: { ...EMPTY_SUMMARY },
      orders: [],
      loading: true,
      loadError: false,
    };
  },
  computed: {
    scopeLabel() {
      return this.scope === "owner" ? "All stores" : "Your stores";
    },
  },
  created() {
    this.loadPayouts();
  },
  methods: {
    async loadPayouts() {
      this.loading = true;
      this.loadError = false;
      try {
        const response = await getDistributorPayouts();
        const data = response ? response.data : null;
        if (data) {
          this.scope = data.scope || "";
          this.summary = data.summary || { ...EMPTY_SUMMARY };
          this.orders = Array.isArray(data.orders) ? data.orders : [];
        } else {
          this.scope = "";
          this.summary = { ...EMPTY_SUMMARY };
          this.orders = [];
        }
      } catch (e) {
        this.loadError = true;
      } finally {
        this.loading = false;
      }
    },
    formatCents(cents) {
      if (typeof cents !== "number") return "$0.00";
      return "$" + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    formatDate(isoString) {
      if (!isoString) return "";
      try {
        return new Date(isoString).toLocaleDateString(undefined, {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch {
        return isoString;
      }
    },
  },
};
</script>

<style scoped>
.dpay {
  max-width: 1040px;
  margin: var(--tf-sp-8, 32px) auto;
  padding: 0 var(--tf-sp-4, 16px);
  color: var(--tf-fg-2, rgba(0, 0, 0, 0.75));
}
.dpay__title {
  font-size: var(--tf-size-h2, 1.5rem);
  font-weight: 600;
  color: var(--tf-fg-1, rgba(0, 0, 0, 0.9));
  margin-bottom: var(--tf-sp-2, 8px);
}
.dpay__scope {
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
  margin-bottom: var(--tf-sp-6, 24px);
}
.dpay__loading {
  font-style: italic;
}
.dpay__error {
  color: var(--tf-error, #c62828);
  font-weight: 600;
}
.dpay__empty {
  padding: var(--tf-sp-6, 24px) var(--tf-sp-4, 16px);
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
  font-style: italic;
}
.dpay__summary {
  display: flex;
  gap: var(--tf-sp-6, 24px);
  margin-bottom: var(--tf-sp-6, 24px);
  flex-wrap: wrap;
}
.dpay__summary-item {
  display: flex;
  flex-direction: column;
  background: var(--tf-surface, #fff);
  border: 1px solid var(--tf-border, rgba(0, 0, 0, 0.12));
  border-radius: var(--tf-radius, 8px);
  padding: var(--tf-sp-4, 16px) var(--tf-sp-6, 24px);
  min-width: 160px;
}
.dpay__summary-item--net {
  border-color: var(--tf-brand, #1a73e8);
}
.dpay__summary-label {
  font-size: var(--tf-size-body-sm, 0.875rem);
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
  margin-bottom: var(--tf-sp-1, 4px);
}
.dpay__summary-value {
  font-size: var(--tf-size-h3, 1.25rem);
  font-weight: 600;
  color: var(--tf-fg-1, rgba(0, 0, 0, 0.9));
}
.dpay__summary-value--fees {
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
}
.dpay__summary-value--net {
  color: var(--tf-brand, #1a73e8);
}
.dpay__table {
  width: 100%;
  border-collapse: collapse;
}
.dpay__th {
  text-align: left;
  padding: var(--tf-sp-2, 8px) var(--tf-sp-3, 12px);
  border-bottom: 2px solid var(--tf-border, rgba(0, 0, 0, 0.12));
  font-size: var(--tf-size-body-sm, 0.875rem);
  color: var(--tf-fg-3, rgba(0, 0, 0, 0.55));
  font-weight: 600;
}
.dpay__th--num {
  text-align: right;
}
.dpay__td {
  padding: var(--tf-sp-3, 12px);
  border-bottom: 1px solid var(--tf-border, rgba(0, 0, 0, 0.08));
  font-size: var(--tf-size-body, 1rem);
}
.dpay__td--num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.dpay__row:last-child .dpay__td {
  border-bottom: none;
}
.dpay__status {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 99px;
  font-size: var(--tf-size-body-sm, 0.875rem);
}
.dpay__status--complete {
  background: rgba(27, 168, 80, 0.12);
  color: #1b6e30;
}
.dpay__status--pending {
  background: rgba(255, 160, 0, 0.12);
  color: #7d5200;
}
</style>
